/**
 * Analyses API
 *
 * Handles all analysis-related API calls.
 * Uses Zod validation for critical endpoints.
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import { getAnalysisDetailResponseSchema } from '@aica/api-types/schemas';
import type {
  AnalysisWithDetails,
  AnalysisPdfReportResponse,
  AnalysisAnnotationsResponse,
  AnalysisAnnotation,
  AnalysisReviewResponse,
  AnalysisReview,
  CreateAnnotationRequest,
} from '../types';

export class AnalysesApi extends BaseApiClient {
  /**
   * Get analysis by ID - validated with Zod
   */
  async getAnalysisById(id: string): Promise<AnalysisWithDetails> {
    const validated = await this.validatedRequest(
      API_ENDPOINTS.ANALYSES_BY_ID(id),
      getAnalysisDetailResponseSchema
    );
    return validated.analysis as unknown as AnalysisWithDetails;
  }

  async getAnalysisPdfReport(id: string): Promise<AnalysisPdfReportResponse> {
    return this.request<AnalysisPdfReportResponse>(API_ENDPOINTS.ANALYSES_PDF_REPORT(id));
  }

  async getAnalysisAnnotations(id: string): Promise<AnalysisAnnotationsResponse> {
    return this.request<AnalysisAnnotationsResponse>(
      API_ENDPOINTS.ANALYSES_ANNOTATIONS(id)
    );
  }

  async createAnnotation(
    analysisId: string,
    data: CreateAnnotationRequest
  ): Promise<{ annotation: AnalysisAnnotation }> {
    return this.request<{ annotation: AnalysisAnnotation }>(
      API_ENDPOINTS.ANALYSES_ANNOTATIONS(analysisId),
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  async getAnalysisReview(analysisId: string): Promise<AnalysisReviewResponse> {
    return this.request<AnalysisReviewResponse>(
      API_ENDPOINTS.ANALYSES_REVIEW(analysisId)
    );
  }

  async createAnalysisReview(
    analysisId: string,
    data: { status: string; markers: string[]; overallFeedback?: string }
  ): Promise<{ review: AnalysisReview }> {
    return this.request<{ review: AnalysisReview }>(
      API_ENDPOINTS.ANALYSES_REVIEW(analysisId),
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  async updateAnnotation(
    analysisId: string,
    annotationId: string,
    data: Partial<CreateAnnotationRequest>
  ): Promise<{ annotation: AnalysisAnnotation }> {
    return this.request<{ annotation: AnalysisAnnotation }>(
      API_ENDPOINTS.ANALYSES_ANNOTATION_BY_ID(analysisId, annotationId),
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
  }

  async deleteAnnotation(analysisId: string, annotationId: string): Promise<void> {
    return this.request<void>(
      API_ENDPOINTS.ANALYSES_ANNOTATION_BY_ID(analysisId, annotationId),
      { method: 'DELETE' }
    );
  }
}
